/**
 * SyncJobsHistoryPanel — F18 T18.3.x.2.
 *
 * Mini-tabela historii ostatnich N job-ów per integracja. Otwierany przez expand button
 * w wierszu `IntegrationsTab`. Pollje `GET /api/erp-integrations/:id/sync-jobs?limit=10`.
 *
 * Kolumny: status (badge) / typ (chip operacji) / kiedy (data + godzina) / agent_id / wynik
 * (ludzkie podsumowanie ze statystyk). Klik wiersza otwiera `SyncJobResultModal` z pełnym
 * widokiem wyniku (kafelki + tabela).
 *
 * Refresh: button „Odśwież" + automatyczny refetch gdy jest pending/running job (co 3s
 * żeby UI łapał update gdy agent kończy job-a w trakcie patrzenia).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { RefreshCw, X } from 'lucide-react';

import { Badge } from '@/components/Badge';
import { IconButton } from '@/components/IconButton';
import { Spinner } from '@/components/Spinner';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

import { formatWhen, syncEntity, syncOp } from './sync-history-helpers';
import { SyncJobResultModal } from './SyncJobResultModal';

// Re-eksport dla istniejących testów logiki (dom funkcji: sync-history-helpers).
export { buildLogEntries, classifyWarning } from './sync-history-helpers';

interface SyncJobRow {
  id: string;
  jobType: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  agentId: string | null;
  result: Record<string, unknown> | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
}

interface Props {
  integrationId: string;
  limit?: number;
}

/** Zwięzłe, ludzkie podsumowanie wyniku do kolumny „Wynik" (bez surowego JSON-a). */
function summarizeResult(job: SyncJobRow, t: (k: string) => string): string {
  if (job.status === 'pending') return t('tenant.settings.integrations.syncHistory.statusPending');
  if (job.status === 'running') return t('tenant.settings.integrations.syncHistory.statusRunning');
  if (!job.result) return '—';

  // test_connection result: { companies: [...] }
  if (job.jobType === 'test_connection' && Array.isArray(job.result.companies)) {
    return t('tenant.settings.integrations.syncHistory.companiesFound').replace(
      '{n}',
      String(job.result.companies.length),
    );
  }

  if (job.status === 'failed' && job.result.error) {
    return String(job.result.error).slice(0, 80);
  }

  // Podsumowanie ze statystyk: „Dodane 3 · Zaktualizowane 17 · Pominięte 5" (tylko niezerowe).
  const stats = (job.result.stats ?? {}) as Record<string, number>;
  const parts: string[] = [];
  const add = (labelKey: string, val: number | undefined) => {
    if (val)
      parts.push(`${t(`tenant.settings.integrations.syncHistory.resultTiles.${labelKey}`)} ${val}`);
  };
  add('added', stats.added);
  add('updated', stats.updated);
  add('sent', stats.pushedToFk);
  add('pulled', stats.updatedLocal);
  add('conflicts', stats.conflictedContractors ?? stats.conflictedEmployees ?? stats.conflicts);
  add('skipped', stats.skipped);
  add('inactive', stats.inactive);
  add('gusFailures', stats.gusFailures);
  // Słowniki (pull-only) — własne liczniki pozycji.
  add('dictSynced', stats.dictionaries_synced);
  add('dictItemsAdded', stats.items_added);
  add('dictItemsUpdated', stats.items_updated);
  add('dictItemsDeactivated', stats.items_deactivated);
  add('dictSkippedMappings', stats.skipped_mappings);
  if (parts.length > 0) return parts.join(' · ');
  return t('tenant.settings.integrations.syncHistory.resultNoChanges');
}

export function SyncJobsHistoryPanel({ integrationId, limit = 10 }: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  // Id przebiegu otwartego w modalu wyniku (drill-down „co zmieniono").
  const [activeJobId, setActiveJobId] = useState<string | null>(null);

  const jobsQ = useQuery({
    queryKey: ['erp-sync-jobs', integrationId, limit],
    queryFn: async () => {
      const res = await api.get<{ jobs: SyncJobRow[]; total: number }>(
        `/erp-integrations/${integrationId}/sync-jobs`,
        { params: { limit } },
      );
      return res.data;
    },
    // Auto-refresh co 3s gdy jest pending/running job w listingu (klient widzi update na żywo)
    refetchInterval: (query) => {
      const jobs = query.state.data?.jobs ?? [];
      const hasActiveJob = jobs.some((j) => j.status === 'pending' || j.status === 'running');
      return hasActiveJob ? 3000 : false;
    },
    refetchOnWindowFocus: false,
  });

  const cancelMutation = useMutation({
    mutationFn: async (jobId: string) => {
      await api.post(`/erp-sync-jobs/${jobId}/cancel`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['erp-sync-jobs', integrationId] });
      toast.show({
        variant: 'success',
        title: t('tenant.settings.integrations.syncHistory.cancelled'),
      });
    },
    onError: (err) => {
      const code = isAxiosError(err) ? (err.response?.data?.error as string | undefined) : null;
      if (code === 'JOB_NOT_PENDING') {
        toast.show({
          variant: 'warning',
          title: t('tenant.settings.integrations.syncHistory.cancelTooLate'),
        });
        queryClient.invalidateQueries({ queryKey: ['erp-sync-jobs', integrationId] });
        return;
      }
      toast.show({
        variant: 'error',
        title: t('tenant.settings.integrations.syncHistory.cancelError'),
      });
    },
  });

  if (jobsQ.isLoading) {
    return (
      <div className="flex items-center justify-center py-3">
        <Spinner />
      </div>
    );
  }

  const jobs = jobsQ.data?.jobs ?? [];

  if (jobs.length === 0) {
    return (
      <div
        className="px-4 py-3 text-xs text-text-tertiary"
        data-testid={`sync-history-empty-${integrationId}`}
      >
        {t('tenant.settings.integrations.syncHistory.empty')}
      </div>
    );
  }

  return (
    <div data-testid={`sync-history-panel-${integrationId}`} className="bg-bg-secondary">
      <div className="flex items-center justify-between border-b border-border-primary px-4 py-2">
        <div className="text-xs font-medium text-text-secondary">
          {t('tenant.settings.integrations.syncHistory.title')}
        </div>
        <IconButton
          variant="ghost"
          size="sm"
          onClick={() => jobsQ.refetch()}
          aria-label={t('tenant.settings.integrations.syncHistory.refresh')}
          title={t('tenant.settings.integrations.syncHistory.refresh')}
          data-testid={`sync-history-refresh-${integrationId}`}
        >
          <RefreshCw aria-hidden="true" />
        </IconButton>
      </div>
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-bg-tertiary text-text-muted">
            <th className="px-4 py-1.5 text-left font-medium">
              {t('tenant.settings.integrations.syncHistory.col.status')}
            </th>
            <th className="px-4 py-1.5 text-left font-medium">
              {t('tenant.settings.integrations.syncHistory.col.type')}
            </th>
            <th className="px-4 py-1.5 text-left font-medium">
              {t('tenant.settings.integrations.syncHistory.col.entity')}
            </th>
            <th className="px-4 py-1.5 text-left font-medium">
              {t('tenant.settings.integrations.syncHistory.col.when')}
            </th>
            <th className="px-4 py-1.5 text-left font-medium">
              {t('tenant.settings.integrations.syncHistory.col.agent')}
            </th>
            <th className="px-4 py-1.5 text-left font-medium">
              {t('tenant.settings.integrations.syncHistory.col.result')}
            </th>
            <th className="px-4 py-1.5"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border-primary">
          {jobs.map((j) => {
            // Wiersz klikalny, gdy jest zapisany wynik do pokazania (nie dla pending/running).
            const hasResult = !!j.result && j.status !== 'pending' && j.status !== 'running';
            return (
              <tr
                key={j.id}
                data-testid={`sync-history-row-${j.id}`}
                className={hasResult ? 'cursor-pointer hover:bg-bg-tertiary/50' : undefined}
                onClick={hasResult ? () => setActiveJobId(j.id) : undefined}
              >
                <td className="px-4 py-1.5">
                  <Badge
                    color={
                      j.status === 'completed' ? 'accent' : j.status === 'failed' ? 'red' : 'gray'
                    }
                  >
                    {t(`tenant.settings.integrations.syncHistory.statusBadge.${j.status}`)}
                  </Badge>
                </td>
                <td className="px-4 py-1.5">
                  <div className="flex items-center gap-1.5">
                    <Badge color={syncOp(j.jobType).color}>
                      {t(`tenant.settings.integrations.syncHistory.op.${syncOp(j.jobType).key}`)}
                    </Badge>
                    <span className="text-text-tertiary">
                      {t(`tenant.settings.integrations.syncHistory.jobType.${j.jobType}`, {
                        defaultValue: j.jobType,
                      })}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-1.5 text-text-secondary">
                  {t(`tenant.settings.integrations.syncHistory.entity.${syncEntity(j.jobType)}`)}
                </td>
                <td className="px-4 py-1.5 text-text-tertiary">{formatWhen(j.createdAt)}</td>
                <td className="px-4 py-1.5 text-text-tertiary">{j.agentId ?? '—'}</td>
                <td className="px-4 py-1.5 text-text-secondary">{summarizeResult(j, t)}</td>
                <td className="px-4 py-1.5 text-right">
                  {j.status === 'pending' && (
                    <IconButton
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        cancelMutation.mutate(j.id);
                      }}
                      disabled={cancelMutation.isPending}
                      aria-label={t('tenant.settings.integrations.syncHistory.cancel')}
                      title={t('tenant.settings.integrations.syncHistory.cancel')}
                      data-testid={`sync-history-cancel-${j.id}`}
                    >
                      <X aria-hidden="true" />
                    </IconButton>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <SyncJobResultModal
        job={(() => {
          const j = jobs.find((x) => x.id === activeJobId);
          if (!j) return null;
          // Przekazujemy też znaczniki czasu — modal pokazuje datę+godzinę przebiegu w nagłówku.
          return {
            id: j.id,
            jobType: j.jobType,
            result: j.result,
            createdAt: j.createdAt,
            finishedAt: j.finishedAt,
          };
        })()}
        onClose={() => setActiveJobId(null)}
      />
    </div>
  );
}
